/**
 * Icône d'installation PWA dans le header.
 *
 * Visible mais non intrusive : n'apparaît que si l'app est réellement
 * installable et pas déjà installée. Un clic déclenche l'installation native
 * (Chromium) ou, sur iOS (aucune API d'installation), ouvre des instructions.
 * Un « coach-mark » différé (2ᵉ session) attire l'œil ; « Plus tard » le
 * reporte à la prochaine session, « × » le retire définitivement — il se
 * replie aussi au premier défilement ou clic ailleurs (sans le retirer).
 *
 * Limite connue : sur iOS, le navigateur n'expose aucun signal « installé »
 * dans l'onglet Safari — l'icône y reste visible même après installation
 * (elle disparaît en revanche dans l'app installée, en mode standalone).
 */
import { useEffect, useRef, useState } from 'react';
import { usePwaInstall } from '../../hooks/usePwaInstall.js';
import { useAppToast } from '../../contexts/AppToastContext.jsx';
import './InstallAppButton.css';

function DownloadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  );
}

function ShareGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v13" />
      <path d="m8 7 4-4 4 4" />
      <rect x="5" y="11" width="14" height="10" rx="2" />
    </svg>
  );
}

export default function InstallAppButton() {
  const {
    installable, canPromptNative, isIOS, promptInstall,
    showCoach, dismissCoach, snoozeCoach,
  } = usePwaInstall();
  const { showSuccess } = useAppToast();
  const [iosHelp, setIosHelp] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const iosCloseRef = useRef(null);

  // Aide iOS (ouverte par l'utilisateur) : focus dedans à l'ouverture, retour
  // au déclencheur à la fermeture — fermable par Échap / clic extérieur.
  useEffect(() => {
    if (!iosHelp) return undefined;
    const trigger = triggerRef.current; // figé maintenant : sûr dans le cleanup
    iosCloseRef.current?.focus();
    const onOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setIosHelp(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setIosHelp(false); };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEsc);
      trigger?.focus();
    };
  }, [iosHelp]);

  // Coach-mark (apparition automatique) : ne doit jamais rester « détaché » —
  // premier défilement, clic ailleurs ou Échap le replient pour la session
  // (snooze, PAS le refus définitif). Évite aussi de recouvrir le panneau de
  // notifications : cliquer la cloche est un clic extérieur → repli.
  useEffect(() => {
    if (!showCoach || iosHelp) return undefined;
    const onOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) snoozeCoach(); };
    const onEsc = (e) => { if (e.key === 'Escape') snoozeCoach(); };
    const onScroll = () => snoozeCoach();
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, { passive: true, once: true });
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll);
    };
  }, [showCoach, iosHelp, snoozeCoach]);

  if (!installable) return null;

  const handleInstall = async () => {
    dismissCoach(); // l'utilisateur a agi sur l'installation : on n'insiste plus
    if (canPromptNative) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        showSuccess('Application installée ! Retrouvez Grand-Électeur sur votre écran d\'accueil.', 'Installé');
      }
    } else if (isIOS) {
      setIosHelp((v) => !v);
    }
  };

  return (
    <div className="install-app" ref={ref}>
      <button
        type="button"
        ref={triggerRef}
        className={`install-app-btn${showCoach ? ' is-coaching' : ''}`}
        onClick={handleInstall}
        aria-label="Installer l'application"
        aria-haspopup={isIOS ? 'dialog' : undefined}
        aria-expanded={isIOS ? iosHelp : undefined}
        title="Installer l'application"
      >
        <DownloadIcon />
      </button>

      {/* Coach-mark : annonce polie (pas un dialogue — aucun focus volé). */}
      {showCoach && !iosHelp && (
        <div className="install-coach" role="status">
          <button
            type="button"
            className="install-coach-close"
            onClick={dismissCoach}
            aria-label="Ne plus proposer l'installation"
          >
            <span aria-hidden="true">×</span>
          </button>
          <p className="install-coach-title">📲 Installez l'application</p>
          <p className="install-coach-text">
            Accès en un geste, plein écran — et le travail terrain continue même sans réseau.
          </p>
          <div className="install-coach-actions">
            <button type="button" className="btn-primary install-coach-cta" onClick={handleInstall}>
              Installer
            </button>
            <button type="button" className="install-coach-later" onClick={snoozeCoach}>
              Plus tard
            </button>
          </div>
        </div>
      )}

      {/* Instructions iOS (aucune API d'installation sur les navigateurs iOS) */}
      {iosHelp && (
        <div className="install-ios-help" role="dialog" aria-label="Installer sur iPhone / iPad">
          <button
            type="button"
            ref={iosCloseRef}
            className="install-coach-close"
            onClick={() => setIosHelp(false)}
            aria-label="Fermer"
          >
            <span aria-hidden="true">×</span>
          </button>
          <p className="install-coach-title">Installer sur iPhone / iPad</p>
          <ol className="install-ios-steps">
            <li>Appuyez sur <ShareGlyph /> <strong>Partager</strong> dans la barre du navigateur.</li>
            <li>Choisissez <strong>« Sur l'écran d'accueil »</strong>.</li>
            <li>Confirmez avec <strong>« Ajouter »</strong>.</li>
          </ol>
        </div>
      )}
    </div>
  );
}
